import { apiClient } from './client';

export interface SessionSummary {
  sessionId: string;
  firstSeen: string;
  lastSeen: string;
  firstMessage: string;
  channel: string;
  requestCount: number;
  totalMs: number | null;
  intents: string[];
  hasOutfit: boolean;
  isVerified: boolean;
  analysisQuality: 'good' | 'degraded' | 'poor' | 'insufficient' | null;
  analyzedAt?: string | null;
  hostname?: string | null;
  siteCountry?: string | null;
  language?: string | null;
}

export interface SessionsSummaryResponse {
  sessions: SessionSummary[];
  unavailableReason?: string;
  total?: number;
  truncated?: boolean;
}

export interface SessionsSummaryParams {
  fromDate?: string;
  toDate?: string;
  fromTime?: string;
  toTime?: string;
  includePlayground?: boolean;
  includeEmpty?: boolean;
}

export interface TurnDebugRouting {
  lane?: string;
  canonicalAction?: string;
  intentType?: string;
  confidence?: number;
  reasons?: string[];
  routingSource?: string;
  fastIntentType?: string;
  gateIntentType?: string;
  conflict?: string;
  retried?: boolean;
  messageShape?: 'single' | 'compound';
  compoundBranches?: Array<{
    clause: string;
    lane: string;
    canonicalAction?: string;
    confidence: number;
  }>;
}

export interface TurnStateSnapshot {
  conversationMode?: string;
  outfitPhase?: string;
  outfitOccasion?: string;
  generalInfoState?: string;
  sessionMode?: string;
  isVerified?: boolean;
  hasOrderNumber?: boolean;
  currentSearchIntent?: {
    categoryId?: string | null;
    searchQuery?: string | null;
  };
  historyLength?: number;
}

export interface TurnDebugInfo {
  routing?: TurnDebugRouting;
  tools?: Array<{
    name: string;
    durationMs: number;
    status: string;
    outputSummary?: string;
  }>;
  latency?: { totalMs: number; toolLatencySum?: number };
  suggestedActions?: Array<{
    label: string;
    value: string;
    actionId?: string;
    intentType?: string;
  }>;
  shownActions?: Array<{
    label: string;
    value: string;
    actionId?: string;
    intentType?: string;
  }>;
  stateSnapshot?: TurnStateSnapshot;
}

export interface SessionTimelineEntry {
  sequence: number;
  sourceIndex: number;
  role: 'user' | 'assistant' | 'system';
  fromAgent?: boolean;
  senderAlias?: string;
  createdAt: string;
  content: string;
  contentLength: number;
  contentPreview: string;
  metadata: ({ _debug?: TurnDebugInfo; responseMs?: number } & Record<string, unknown>) | null;
  attachments?: Array<{ url: string; uploadedAt?: string }>;
}

export interface SessionOutfitSelection {
  productId: string;
  productName: string;
  brand?: string;
  price?: number;
  currency?: string;
  url?: string;
  imageUrl?: string;
  categoryKey?: string;
  selectedAt?: string;
}

export interface SessionLogDetails {
  sessionId: string;
  fetchedAt: string;
  summary: {
    id: string;
    messageCount: number;
    updatedAt: string;
    expiresAt: string;
    currentOrder?: {
      orderNumber?: string;
      postalCode?: string;
      isVerified?: boolean;
    };
  };
  state: {
    id: string;
    channel?: string;
    createdAt: string;
    updatedAt: string;
    expiresAt: string;
    status?: string;
    endedAt?: string;
    endReason?: string;
    language?: string;
    uiLanguage?: string;
    metadata?: Record<string, unknown>;
    userPreferences?: Record<string, unknown>;
    currentOrder?: {
      orderNumber?: string;
      postalCode?: string;
      isVerified?: boolean;
    };
    outfitState?: {
      phase?: string;
      currency?: string;
      totalPrice?: number;
      occasionLabel?: string;
      selections?: Record<string, SessionOutfitSelection>;
      rejectedProductIds?: string[];
      outfitContext?: {
        freeText?: string;
        occasion?: string;
        season?: string;
        colors?: string[];
        materials?: string[];
        selectedArticleGroups?: Array<{ id: number; name: string; slot: string }>;
        categorySequence?: string[];
      };
    };
    messageCount: number;
  };
  totals: {
    messageCount: number;
    userMessages: number;
    assistantMessages: number;
    averageResponseMs: number | null;
    maxResponseMs: number | null;
  };
  sessionFlags?: {
    emailDraftStatus: string | null;
    emailDraftSentAt: string | null;
    emailDraftConfirmedAt: string | null;
    handoffStatus: string | null;
    handoffViaMail: boolean | null;
    caseType: string | null;
    clientSurface: string | null;
  };
  timeline: SessionTimelineEntry[];
}

export const getSessionsSummary = async (
  params: SessionsSummaryParams = {},
): Promise<SessionsSummaryResponse> => {
  const search = new URLSearchParams();
  if (params.fromDate) search.set('fromDate', params.fromDate);
  if (params.toDate) search.set('toDate', params.toDate);
  if (params.fromTime) search.set('fromTime', params.fromTime);
  if (params.toTime) search.set('toTime', params.toTime);
  if (params.includePlayground) search.set('includePlayground', 'true');
  if (params.includeEmpty) search.set('includeEmpty', 'true');

  const { data } = await apiClient.get<SessionsSummaryResponse>(
    `/sessions/summary?${search.toString()}`,
  );
  return data;
};

export const getSessionLogDetails = async (sessionId: string): Promise<SessionLogDetails> => {
  const { data } = await apiClient.get<SessionLogDetails>(`/sessions/${sessionId}/logs`);
  return data;
};

// Radering på kundens begäran (GDPR art. 17). Går inte att ångra.
export const deleteSessionChatImages = async (sessionId: string): Promise<{ deleted: number }> => {
  const { data } = await apiClient.delete<{ deleted: number }>(`/sessions/${sessionId}/chat-images`);
  return data;
};

export const deleteCustomerChatImages = async (customerId: string): Promise<{ deleted: number }> => {
  const { data } = await apiClient.delete<{ deleted: number }>('/sessions/chat-images', {
    params: { customerId },
  });
  return data;
};

export const isCustomerNumber = (value: string): boolean => /^[0-9]{1,15}$/.test(value.trim());

export type SessionAnalysisQuality = 'good' | 'degraded' | 'poor';
export type SessionAnalysisCategory = 'routing' | 'tool' | 'content' | 'latency' | 'ux';
export type SessionAnalysisSeverity = 'low' | 'med' | 'high';

export interface SessionAnalysisIssue {
  turnSequence?: number;
  category: SessionAnalysisCategory;
  severity: SessionAnalysisSeverity;
  description: string;
}

export interface SessionAnalysis {
  quality: SessionAnalysisQuality;
  summary: string;
  issues: SessionAnalysisIssue[];
  recommendations: string[];
  analyzedAt: string;
  model: string;
  timelineLength: number;
}

export interface AnalyzeSessionResponse {
  analysis: SessionAnalysis;
  cached: boolean;
}

export const analyzeSession = async (
  sessionId: string,
  refresh = false,
): Promise<AnalyzeSessionResponse> => {
  const query = refresh ? '?refresh=true' : '';
  const { data } = await apiClient.post<AnalyzeSessionResponse>(
    `/sessions/${sessionId}/analyze${query}`,
  );
  return data;
};

export interface BatchAnalyzeResponse {
  requested: number;
  analyzed: number;
  failed: number;
  qualityCounts: {
    good: number;
    degraded: number;
    poor: number;
    insufficient: number;
  };
}

export const batchAnalyzeSessions = async (opts: {
  days?: number;
  limit?: number;
  onlyUnanalyzed?: boolean;
}): Promise<BatchAnalyzeResponse> => {
  const params = new URLSearchParams();
  if (opts.days !== undefined) params.set('days', String(opts.days));
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts.onlyUnanalyzed === false) params.set('onlyUnanalyzed', 'false');
  const qs = params.toString();
  const { data } = await apiClient.post<BatchAnalyzeResponse>(
    `/sessions/batch-analyze${qs ? `?${qs}` : ''}`,
  );
  return data;
};
